import type { DragEvent, ChangeEvent } from 'react';
import React, { useState, useRef, useEffect } from 'react';
import { uploadImage } from '../../../services/upload.service';
import styles from './ImageUploader.module.css';

export interface ImageUploaderProps {
  value?: string | null;
  onChange: (url: string | null) => void;
  onUploadStart?: () => void;
  onUploadComplete?: (url: string) => void;
  onUploadError?: (error: string) => void;
  folder?: string;
  maxSize?: number; // in MB
  disabled?: boolean;
  label?: string;
  helpText?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  value,
  onChange,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  folder = 'images',
  maxSize = 10,
  disabled = false,
  label,
  helpText,
}) => {
  const [preview, setPreview] = useState<string | null>(value || null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync preview with value prop when it changes (e.g., when editing existing data)
  useEffect(() => {
    if (value && value !== preview) {
      setPreview(value);
    }
  }, [value, preview]);

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const maxSizeBytes = maxSize * 1024 * 1024;

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Please select a valid image file';
    }

    // Check file size
    if (file.size > maxSizeBytes) {
      return `File size exceeds maximum of ${maxSize}MB`;
    }

    return null;
  };

  const handleFile = async (file: File) => {
    setError(null);

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      onUploadError?.(validationError);
      return;
    }

    // Create preview
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    // Upload file
    setUploading(true);
    setProgress(0);
    onUploadStart?.();

    // Simulate progress (since we don't have real progress from fetch)
    const progressInterval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 10, 90));
    }, 200);

    try {
      const result = await uploadImage(file, { folder });

      // Debug: Log upload result
      console.log('[ImageUploader] Upload result:', result);

      clearInterval(progressInterval);
      setProgress(100);

      if (result.success && result.data) {
        console.log('[ImageUploader] Success! URL:', result.data.url);
        onChange(result.data.url);
        onUploadComplete?.(result.data.url);
      } else {
        const errorMessage = result.error?.message || 'Failed to upload image';
        console.error('[ImageUploader] Upload failed:', errorMessage);
        setError(errorMessage);
        onUploadError?.(errorMessage);
        setPreview(null);
      }
    } catch (err) {
      clearInterval(progressInterval);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to upload image';
      setError(errorMessage);
      onUploadError?.(errorMessage);
      setPreview(null);
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploading) return;

    const files = e.dataTransfer.files;
    const file = files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled || uploading) return;

    const files = e.target.files;
    const file = files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={styles.imageUploader}>
      {label && <label className={styles.label}>{label}</label>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className={styles.fileInput}
        disabled={disabled || uploading}
      />

      {!preview && (
        <div
          className={`${styles.uploadArea} ${dragActive ? styles.dragActive : ''} ${disabled ? styles.disabled : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div className={styles.uploadContent}>
            <svg
              className={styles.uploadIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <span className={styles.uploadText}>
              {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </span>
            <span className={styles.uploadHint}>
              {helpText || `PNG, JPG, GIF up to ${maxSize}MB`}
            </span>
          </div>
        </div>
      )}

      {preview && (
        <div className={styles.preview}>
          <img src={preview} alt="Preview" className={styles.previewImage} />
          <div className={styles.previewActions}>
            <button
              type="button"
              onClick={handleClick}
              className={styles.previewButton}
              disabled={disabled || uploading}
              title="Modifier l'image"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className={styles.previewButton}
              disabled={uploading}
              title="Supprimer l'image"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {uploading && (
        <div className={styles.progress}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className={styles.progressText}>Uploading... {progress}%</p>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};
